import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Mail } from "lucide-react";
import { signUpSchema, signInSchema } from "@/lib/validation";
import { z } from "zod";
import { MFAVerification } from "@/components/auth/MFAVerification";
import { generateDeviceFingerprint } from "@/lib/deviceFingerprint";
import { LandingHeader } from "@/components/layout/LandingHeader";
import { OptimizedLogo } from "@/components/ui/OptimizedLogo";
import { SignupSuccessModal } from "@/components/auth/SignupSuccessModal";
import { useDraftFormPersistence } from "@/hooks/useDraftPersistence";
import { TIER_PRICING, type SubscriptionTier, getTierPrice } from "@/lib/pricing";
import { Badge } from "@/components/ui/badge";
import { CreditCard } from "lucide-react";
import { SignupTestimonialCard } from "@/components/auth/SignupTestimonialCard";

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Draft persistence for email and fullName only (NOT password for security)
  const { formData, updateField, clearDraft } = useDraftFormPersistence('auth-form', {
    email: '',
    fullName: '',
  });
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaUserId, setMfaUserId] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetAttempts, setResetAttempts] = useState(0);
  const [pendingInvitation, setPendingInvitation] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showSignupSuccess, setShowSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  
  // Get plan parameters from URL
  const urlParams = new URLSearchParams(location.search);
  const planParam = urlParams.get('plan');
  const billingParam = urlParams.get('billing');
  const tabParam = urlParams.get('tab');
  const nextParam = urlParams.get('next');
  const nextPath = nextParam && nextParam.startsWith('/') ? nextParam : null;

  // Determine if this is a paid plan signup flow
  const isPaidPlanFlow = Boolean(planParam && billingParam);
  
  // Set initial active tab based on URL param - default to signup for paid plan flow
  useEffect(() => {
    if (isPaidPlanFlow) {
      setActiveTab("signup");
    } else {
      setActiveTab(tabParam === "signup" ? "signup" : "signin");
    }
  }, [tabParam, isPaidPlanFlow]);

  // Check for pending invitation on mount
  useEffect(() => {
    const token = localStorage.getItem("pending_invitation_token");
    if (token) {
      fetchPendingInvitation(token);
    }
  }, []);

  const fetchPendingInvitation = async (token: string) => {
    try {
      const { data } = await supabase
        .from("team_invitations")
        .select(`
          *,
          companies:company_id (name)
        `)
        .eq("token", token)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .single();

      if (data) {
        setPendingInvitation(data);
        updateField('email', data.email);
      }
    } catch (err) {
      console.error("Error fetching pending invitation:", err);
    }
  };

  const navigateAfterAuth = async (options?: { replace?: boolean }) => {
    const replace = options?.replace ?? false;

    // If a next path was provided (e.g. from email verification), always honor it.
    // This ensures /unlock-report receives verified users correctly.
    if (nextPath) {
      navigate(nextPath, { replace });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user) {
      navigate('/dashboard', { replace });
      return;
    }

    // Block unverified email/password users — Google OAuth users don't have this flag
    if (session.user.user_metadata?.email_verified === false) {
      await supabase.auth.signOut(); // Ensure session is cleared for unverified users
      navigate('/auth', { replace });
      return;
    }

    const userId = session.user.id;
    const userCreatedAt = new Date(session.user.created_at);
    const now = new Date();
    const minutesSinceCreation = (now.getTime() - userCreatedAt.getTime()) / (1000 * 60);

    const { data: preferences } = await supabase
      .from('user_preferences')
      .select('onboarding_completed')
      .eq('user_id', userId)
      .maybeSingle();

    const isNewUser = minutesSinceCreation < 5 || !preferences?.onboarding_completed;

    navigate(isNewUser ? '/welcome' : '/dashboard', { replace });
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate all inputs using zod schema
      const validatedData = signUpSchema.parse({
        email: formData.email.trim(),
        password,
        confirmPassword,
        fullName: formData.fullName.trim(),
        termsAccepted,
      });

      // Get client IP and device fingerprint for abuse prevention
      const deviceFingerprint = navigator.userAgent + navigator.language + screen.colorDepth;
      
      // Call validate-signup function
      const { data: validationResult, error: validationError } = await supabase.functions.invoke(
        'validate-signup',
        {
          body: {
            email: validatedData.email,
            ipAddress: 'client-ip', // Will be determined server-side
            deviceFingerprint: btoa(deviceFingerprint), // Base64 encode
            userAgent: navigator.userAgent,
          },
        }
      );

      if (validationError || !validationResult?.allowed) {
        toast({
          variant: "destructive",
          title: "Signup Blocked",
          description: validationResult?.reason || "Unable to create account at this time.",
        });
        setLoading(false);
        return;
      }

      // Create the account (auto_confirm is now enabled, so account is confirmed)
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: validatedData.email,
        password: validatedData.password,
        options: {
          data: {
            full_name: validatedData.fullName,
            email_verified: false, // Mark as unverified initially
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error("Failed to create account");

      // Clear any stale preview analysis so new users start clean
      try {
        localStorage.removeItem('pending_preview_analysis');
        sessionStorage.removeItem('pending_preview_analysis');
        localStorage.removeItem('pending_preview_token');
      } catch { /* ignore */ }

      // Generate verification token and send email — token is stored server-side
      // by the edge function (which has service role) to avoid 401 race condition
      // that occurs when the client session isn't yet active right after signUp().
      const verificationToken = crypto.randomUUID() + '-' + Date.now().toString(36);

      try {
        const { error: emailError } = await supabase.functions.invoke('send-signup-confirmation', {
          body: {
            email: validatedData.email,
            name: validatedData.fullName,
            token: verificationToken,
            userId: authData.user.id,
            siteUrl: window.location.origin,
          },
        });

        if (emailError) {
          console.error('Error sending verification email:', emailError);
          // Don't throw - user is created, just log the error
        }
      } catch (emailErr) {
        console.error('Exception sending verification email:', emailErr);
        // Don't throw - user is created, just log the error
      }

      // Get trial parameter from URL
      const trialParam = urlParams.get('trial');
      
      // Only sign out if NOT going to paid checkout or trial signup
      // For paid plans, keep user signed in for Stripe checkout
      // For free trial, sign out and redirect to dashboard
      if (trialParam === 'true') {
        // Free trial signup - sign out immediately before any navigation to prevent
        // race condition where auto-confirm session lets unverified user into dashboard
        await supabase.auth.signOut();
        
        toast({
          title: "Account Created!",
          description: `We've sent a verification email to ${validatedData.email}. Please check your inbox and click the link to activate your account.`,
          duration: 10000,
        });
        
        // Stay on /auth — do NOT navigate to a protected route before verification
        setActiveTab("signin");
        updateField('email', validatedData.email);
        setPassword('');
        setConfirmPassword('');
        updateField('fullName', '');
        return;
      } else if (!planParam || !billingParam) {
        // Regular signup without plan - sign out
        await supabase.auth.signOut();
      }
      // If planParam and billingParam exist, keep user signed in for checkout

      // Check if user came from pricing page with plan selection
      if (planParam && billingParam && authData.user) {
        const tierName = planParam;
        
        toast({
          title: "Account Created!",
          description: "Redirecting to checkout...",
        });
        
        console.log("[Auth] Starting checkout with params:", { tierName, billingParam });
        
        // Wait for session to be fully established
        const waitForSession = async () => {
          let attempts = 0;
          const maxAttempts = 10;
          
          while (attempts < maxAttempts) {
            const { data: { session } } = await supabase.auth.getSession();
            
            if (session?.access_token) {
              console.log("[Auth] Session verified, starting checkout...");
              
              try {
                toast({
                  title: "Setting up payment...",
                  description: "Redirecting to secure checkout",
                });
                
                const { data, error } = await supabase.functions.invoke("create-checkout-session", {
                  body: { tierName, billingPeriod: billingParam },
                });
                
                if (error) throw error;
                
                if (data?.url) {
                  console.log("[Auth] Redirecting to Stripe checkout");
                  window.location.href = data.url;
                  return;
                } else {
                  throw new Error("No checkout URL returned");
                }
              } catch (checkoutError: any) {
                console.error("Checkout error:", checkoutError);
                toast({
                  title: "Error",
                  description: "Failed to start checkout. Please try again from the pricing page.",
                  variant: "destructive",
                });
                navigate("/pricing");
                return;
              }
            }
            
            // Wait 500ms before next attempt
            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
          }
          
          // If we get here, session never established
          console.error("[Auth] Session not established after 5 seconds");
          toast({
            title: "Session Error",
            description: "Please log in and try again from the pricing page.",
            variant: "destructive",
          });
          navigate("/pricing");
        };
        
        waitForSession();
        return; // Don't show the standard success message
      }

      // Check for pending invitation
      const pendingToken = localStorage.getItem("pending_invitation_token");
      if (pendingToken && authData.user) {
        try {
          // Navigate to accept invite page to complete the flow
          navigate(`/accept-invite/${pendingToken}`);
          return;
        } catch (err) {
          console.error("Error processing pending invitation:", err);
        }
      }

      toast({
        title: "Welcome to Auditic!",
        description: `We've sent a verification email to ${validatedData.email}. Please check your inbox and click the verification link to activate your account.`,
        duration: 10000,
      });

      // Switch to sign in tab and clear the form
      setActiveTab("signin");
      updateField('email', validatedData.email); // Pre-fill email for sign in
      setPassword('');
      setConfirmPassword('');
      updateField('fullName', '');
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0]?.message || "Please check your input",
          variant: "destructive",
        });
      } else {
        // Check for specific "user already exists" error
        const errorMessage = error.message?.toLowerCase() || '';
        if (errorMessage.includes('already') || errorMessage.includes('exist') || error.code === '23505') {
          toast({
            title: "Email Already Registered",
            description: (
              <div className="space-y-2">
                <p>This email is already registered. Would you like to sign in instead?</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setActiveTab("signin");
                    // Email already in formData
                  }}
                  className="w-full mt-2"
                >
                  Go to Sign In
                </Button>
              </div>
            ),
            variant: "destructive",
            duration: 8000,
          });
        } else {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate all inputs using zod schema
      const validatedData = signInSchema.parse({
        email: formData.email.trim(),
        password,
      });

      const { error, data } = await supabase.auth.signInWithPassword({
        email: validatedData.email,
        password: validatedData.password,
      });

      if (error) {
        // Handle unconfirmed email specifically — give user a clear, actionable message
        if (error.message?.toLowerCase().includes('email not confirmed')) {
          toast({
            title: "Email Not Verified",
            description: "Please check your inbox and click the verification link before signing in. Check your spam folder if you don't see it.",
            duration: 10000,
          });
          setLoading(false);
          return;
        }
        throw error;
      }

      // Check if user has MFA enabled by checking for active factors
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const hasMfaFactor = factors?.totp && factors.totp.length > 0;

      const userId = data.user?.id;
      if (userId && hasMfaFactor) {
        // User has MFA enabled - check for trusted device
        const deviceFingerprint = generateDeviceFingerprint();
        
        const { data: trustedDevice } = await supabase
          .from('trusted_devices')
          .select('*')
          .eq('user_id', userId)
          .eq('device_fingerprint', deviceFingerprint)
          .gte('trusted_until', new Date().toISOString())
          .single();

        if (!trustedDevice) {
          // Device not trusted - require MFA verification
          setMfaUserId(userId);
          setShowMFA(true);
          setLoading(false);
          return;
        }

        // Update last used timestamp for trusted device
        await supabase
          .from('trusted_devices')
          .update({ last_used_at: new Date().toISOString() })
          .eq('id', trustedDevice.id);
      }

      // Check if user was trying to start a trial
      if (planParam && billingParam) {
        const tierName = planParam;
        
        toast({
          title: "Signed In!",
          description: "Redirecting to checkout...",
        });
        
        try {
          console.log("[Auth] Starting checkout session creation...");
          toast({
            title: "Setting up payment...",
            description: "Redirecting to secure checkout",
          });
          
          const { data, error } = await supabase.functions.invoke("create-checkout-session", {
            body: { tierName, billingPeriod: billingParam },
          });
          
          console.log("[Auth] Checkout response:", { data, error });
          
          if (error) {
            console.error("[Auth] Checkout error:", error);
            throw error;
          }
          
          if (data?.url) {
            console.log("[Auth] Checkout URL received, redirecting to:", data.url);
            // Use location.assign for most reliable redirect
            window.location.assign(data.url);
            return;
          }
          
          console.error("[Auth] No URL in response:", data);
        } catch (checkoutError: any) {
          console.error("Checkout error:", checkoutError);
          toast({
            title: "Error",
            description: "Failed to start checkout. Please try again from the pricing page.",
            variant: "destructive",
          });
        }
      }

      toast({
        title: "Welcome back!",
        description: "Signed in successfully.",
        duration: 2000,
      });

      // Redirect to the appropriate post-auth destination
      await navigateAfterAuth();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0]?.message || "Please check your input",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = () => {
    setShowMFA(false);
    setMfaUserId(null);
    toast({
      title: "Welcome back!",
      description: "Signed in successfully.",
      duration: 2000,
    });
    void navigateAfterAuth();
  };

  const handleMFACancel = async () => {
    setShowMFA(false);
    setMfaUserId(null);
    await supabase.auth.signOut();
    toast({
      title: "Sign in cancelled",
      description: "You have been signed out.",
    });
  };

  // Check for existing session on mount and redirect if authenticated
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        await navigateAfterAuth({ replace: true });
      }
    };

    checkSession();
  }, [navigate, nextPath]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
        },
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign in with Google",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (resetAttempts >= 3) {
      toast({
        title: "Too many attempts",
        description: "Please try again later.",
        variant: "destructive",
      });
      return;
    }

    setResetLoading(true);
    
    try {
      // Call custom password reset function
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: {
          email: resetEmail
        }
      });
      
      if (error) throw error;
      
      setResetSent(true);
      setResetAttempts(prev => prev + 1);
      toast({
        title: "Email sent!",
        description: "Check your email for password reset instructions.",
      });
    } catch (error: any) {
      setResetAttempts(prev => prev + 1);
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  // Show MFA verification if needed
  if (showMFA && mfaUserId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500/5 via-background to-cyan-500/5 dark:from-purple-500/10 dark:via-background dark:to-cyan-500/10 p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>
        <div className="relative z-10">
          <MFAVerification onSuccess={handleMFASuccess} onCancel={handleMFACancel} />
        </div>
      </div>
    );
  }

  // Show password reset flow if needed
  if (showPasswordReset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500/5 via-background to-cyan-500/5 dark:from-purple-500/10 dark:via-background dark:to-cyan-500/10 p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>
        
        <div className="w-full max-w-md space-y-4 relative z-10 animate-fade-in">
          <Button
            variant="outline"
            onClick={() => {
              setShowPasswordReset(false);
              setResetSent(false);
              setResetEmail("");
            }}
            className="gap-2 hover-lift neon-border"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sign In
          </Button>
          
          <Card className="w-full glass-card neon-border shadow-glass">
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>
                {resetSent 
                  ? "Check your email for reset instructions" 
                  : "Enter your email to receive a password reset link"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!resetSent ? (
                <form onSubmit={handlePasswordResetRequest} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={resetLoading || resetAttempts >= 3}
                  >
                    {resetLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                  {resetAttempts >= 3 && (
                    <p className="text-sm text-destructive text-center">
                      Too many attempts. Please try again later.
                    </p>
                  )}
                </form>
              ) : (
                <div className="space-y-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    We've sent a password reset link to <strong>{resetEmail}</strong>. 
                    Check your email and click the link to reset your password.
                  </p>
                  <p className="text-sm font-bold text-primary mt-3 p-3 bg-primary/10 rounded-md border border-primary/20">
                    ⚠️ Please check your spam/junk folder if you don't see the email within a few minutes.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The link will expire in 1 hour.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Helper to get plan display info
  const getPlanDisplayInfo = () => {
    if (!planParam || !billingParam) return null;
    
    const tierName = planParam.charAt(0).toUpperCase() + planParam.slice(1);
    const tier = planParam as SubscriptionTier;
    
    // Check if it's a valid tier
    if (!TIER_PRICING[tier]) return null;
    
    const price = getTierPrice(tier, billingParam as "monthly" | "annual");
    const billingText = billingParam === "annual" ? "/year" : "/month";
    
    return {
      tierName,
      price,
      billingText,
      billingPeriod: billingParam,
    };
  };

  const planInfo = getPlanDisplayInfo();

  // Paid plan signup flow - show both signin and signup tabs
  if (isPaidPlanFlow) {
    return (
      <>
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-500/5 via-background to-cyan-500/5 dark:from-purple-500/10 dark:via-background dark:to-cyan-500/10 relative overflow-hidden">
          <LandingHeader />
          
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
          </div>
          
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-md space-y-4 relative z-10 animate-fade-in">
              <Button
                variant="outline"
                onClick={() => navigate("/pricing")}
                className="gap-2 hover-lift neon-border"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Pricing
              </Button>
              
              {/* Plan Selection Banner */}
              {planInfo && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
                  <div className="p-2 bg-primary/20 rounded-full">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      You're signing up for the <span className="text-primary font-semibold">{planInfo.tierName}</span> plan
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${planInfo.price}{planInfo.billingText} • You'll proceed to checkout after
                    </p>
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {planInfo.billingPeriod === "annual" ? "Annual" : "Monthly"}
                  </Badge>
                </div>
              )}
              
              <Card className="w-full glass-card shadow-glass border border-border/50 shadow-[0_0_20px_hsl(var(--primary)/0.1),0_8px_32px_hsl(var(--primary)/0.08)] backdrop-blur-sm transition-shadow hover:shadow-[0_0_25px_hsl(var(--primary)/0.15),0_12px_40px_hsl(var(--primary)/0.12)]">
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl font-bold">
                    Get Started with Auditic
                  </CardTitle>
                  <CardDescription>
                    Create an account or sign in to continue to checkout
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Tabs for Sign In / Sign Up */}
                  <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "signin" | "signup")} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="signup">Create Account</TabsTrigger>
                      <TabsTrigger value="signin">Sign In</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="signup" className="space-y-4 mt-4">
                      {/* Google Sign Up */}
                      <Button
                        type="button"
                        className="w-full h-12 gap-2 bg-foreground text-background hover:bg-foreground/90 border-0"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                      >
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        {loading ? "Connecting..." : "Continue with Google"}
                      </Button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-card px-2 text-muted-foreground">Or with email</span>
                        </div>
                      </div>
                      
                      {/* Sign Up Form */}
                      <form onSubmit={handleSignUp} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="signup-name">Full Name</Label>
                          <Input
                            id="signup-name"
                            type="text"
                            placeholder="Your full name"
                            value={formData.fullName}
                            onChange={(e) => updateField('fullName', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">Email</Label>
                          <Input
                            id="signup-email"
                            type="email"
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-password">Password</Label>
                          <Input
                            id="signup-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                          <Input
                            id="signup-confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                        </div>
                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="terms"
                            checked={termsAccepted}
                            onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                          />
                          <Label htmlFor="terms" className="text-sm leading-tight cursor-pointer">
                            I agree to the{" "}
                            <Link to="/terms" className="text-primary underline" target="_blank">
                              Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy" className="text-primary underline" target="_blank">
                              Privacy Policy
                            </Link>
                          </Label>
                        </div>
                        <Button type="submit" className="w-full" disabled={loading || !termsAccepted}>
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Creating account...
                            </>
                          ) : (
                            <>
                              Create Account & Continue to Checkout
                            </>
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                    
                    <TabsContent value="signin" className="space-y-4 mt-4">
                      {/* Google Sign In */}
                      <Button
                        type="button"
                        className="w-full h-12 gap-2 bg-foreground text-background hover:bg-foreground/90 border-0"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                      >
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        {loading ? "Connecting..." : "Continue with Google"}
                      </Button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-card px-2 text-muted-foreground">Or with email</span>
                        </div>
                      </div>
                      
                      {/* Sign In Form */}
                      <form onSubmit={handleSignIn} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="signin-email">Email</Label>
                          <Input
                            id="signin-email"
                            type="email"
                            placeholder="you@example.com"
                            value={formData.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signin-password">Password</Label>
                          <Input
                            id="signin-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <div className="flex justify-end mt-1">
                            <Button
                              type="button"
                              variant="link"
                              className="text-sm px-0 h-auto"
                              onClick={() => setShowPasswordReset(true)}
                            >
                              Forgot Password?
                            </Button>
                          </div>
                        </div>
                        <Button type="submit" className="w-full" disabled={loading}>
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Signing in...
                            </>
                          ) : (
                            <>
                              Sign In & Continue to Checkout
                            </>
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
              
              {/* Small reassurance text */}
              <p className="text-center text-xs text-muted-foreground/60 mt-4">
                Secure checkout powered by Stripe. Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Free trial signup flow - two-column layout with testimonial
  if (tabParam === "signup") {
    return (
      <>
        <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
          
          
          <div className="flex-1 flex items-center justify-center p-4 lg:py-6 lg:px-0">
            <div className="w-full max-w-5xl flex rounded-2xl overflow-visible shadow-[0_0_40px_hsl(var(--primary)/0.08)] border border-border/40 relative z-10 animate-fade-in">
              
              {/* Left Column - Form */}
              <div className="w-full lg:w-[55%] bg-card p-6 lg:p-8 flex flex-col justify-center rounded-l-2xl overflow-hidden">
                <div className="mb-4">
                   <div className="flex items-center justify-center gap-1.5 mb-4">
                     <span className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">Welcome to</span>
                     <OptimizedLogo size="lg" className="h-16 md:h-[72px] object-contain" />
                  </div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground tracking-tight text-center">Discovery → Deals</h2>
                  <p className="text-muted-foreground text-base md:text-lg mt-0.5 text-center">Create your free account to get started</p>
                </div>

                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "signin" | "signup")} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-3">
                    <TabsTrigger value="signup">Create Account</TabsTrigger>
                    <TabsTrigger value="signin">Sign In</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="signup" className="space-y-4 mt-0">
                    {/* Google Sign Up */}
                    <Button
                      type="button"
                      className="w-full h-11 gap-2 bg-foreground text-background hover:bg-foreground/90 border-0 rounded-lg font-medium"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      {loading ? "Connecting..." : "Continue with Google"}
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or with email</span>
                      </div>
                    </div>
                    
                    {/* Sign Up Form */}
                    <form onSubmit={handleSignUp} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="signup-firstname-trial" className="text-xs font-medium">First Name</Label>
                          <Input
                            id="signup-firstname-trial"
                            type="text"
                            placeholder="Jane"
                            value={formData.fullName.split(' ')[0] || ''}
                            onChange={(e) => {
                              const last = formData.fullName.split(' ').slice(1).join(' ');
                              updateField('fullName', (e.target.value + (last ? ' ' + last : '')).trimStart());
                            }}
                            required
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="signup-lastname-trial" className="text-xs font-medium">Last Name</Label>
                          <Input
                            id="signup-lastname-trial"
                            type="text"
                            placeholder="Smith"
                            value={formData.fullName.split(' ').slice(1).join(' ') || ''}
                            onChange={(e) => {
                              const first = formData.fullName.split(' ')[0] || '';
                              updateField('fullName', (first + ' ' + e.target.value).trimStart());
                            }}
                            required
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="signup-email-trial" className="text-xs font-medium">Email Address</Label>
                        <Input
                          id="signup-email-trial"
                          type="email"
                          placeholder="you@company.com"
                          value={formData.email}
                          onChange={(e) => updateField('email', e.target.value)}
                          required
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="signup-password-trial" className="text-xs font-medium">Password</Label>
                        <Input
                          id="signup-password-trial"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="signup-confirm-password-trial" className="text-xs font-medium">Confirm Password</Label>
                        <Input
                          id="signup-confirm-password-trial"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          className="h-10"
                        />
                      </div>
                      <div className="flex items-start space-x-2 pt-1">
                        <Checkbox
                          id="terms-trial"
                          checked={termsAccepted}
                          onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                        />
                        <Label htmlFor="terms-trial" className="text-xs leading-tight cursor-pointer text-muted-foreground">
                          I agree to the{" "}
                          <Link to="/terms" className="text-primary underline" target="_blank">
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link to="/privacy" className="text-primary underline" target="_blank">
                            Privacy Policy
                          </Link>
                        </Label>
                      </div>
                      <Button type="submit" className="w-full h-11 font-medium rounded-lg" disabled={loading || !termsAccepted}>
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating account...
                          </>
                        ) : (
                          "Create Free Account"
                        )}
                      </Button>
                    </form>
                    
                    {/* Trust signals */}
                    <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <svg className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        7-day free trial
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        No credit card
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        Cancel anytime
                      </span>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="signin" className="space-y-4 mt-0">
                    {/* Google Sign In */}
                    <Button
                      type="button"
                      className="w-full h-11 gap-2 bg-foreground text-background hover:bg-foreground/90 border-0 rounded-lg font-medium"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      {loading ? "Connecting..." : "Continue with Google"}
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or sign in with email</span>
                      </div>
                    </div>
                    
                    {/* Sign In Form */}
                    <form onSubmit={handleSignIn} className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="signin-email-trial" className="text-xs font-medium">Email</Label>
                        <Input
                          id="signin-email-trial"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={(e) => updateField('email', e.target.value)}
                          required
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="signin-password-trial" className="text-xs font-medium">Password</Label>
                        <Input
                          id="signin-password-trial"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="h-10"
                        />
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="remember-me-trial"
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                            />
                            <Label htmlFor="remember-me-trial" className="text-xs cursor-pointer">
                              Remember me
                            </Label>
                          </div>
                          <Button
                            type="button"
                            variant="link"
                            className="text-xs px-0 h-auto"
                            onClick={() => setShowPasswordReset(true)}
                          >
                            Forgot Password?
                          </Button>
                        </div>
                      </div>
                      <Button type="submit" variant="outline" className="w-full h-11 border-foreground/30 text-foreground hover:bg-foreground hover:text-background rounded-lg font-medium" disabled={loading}>
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          "Sign In"
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>

               {/* Right Column - Testimonial / Social Proof */}
              <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-br from-[hsl(250,60%,62%,0.15)] via-[hsl(200,80%,55%,0.10)] to-[hsl(280,50%,60%,0.12)] dark:from-[hsl(250,60%,50%,0.25)] dark:via-[hsl(200,80%,45%,0.18)] dark:to-[hsl(280,50%,55%,0.20)] p-10 flex-col justify-center items-center relative overflow-hidden">
                {/* Decorative blobs */}
                <div className="absolute top-10 right-10 w-48 h-48 bg-[hsl(250,70%,65%,0.12)] rounded-full blur-3xl animate-float" />
                <div className="absolute bottom-16 left-8 w-36 h-36 bg-[hsl(200,80%,55%,0.10)] rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-[hsl(280,50%,60%,0.08)] rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
                
                {/* Rotating Testimonial Card */}
                <SignupTestimonialCard />
              </div>

            </div>
          </div>
        </div>
      </>
    );
  }

  // Normal sign in flow (sign-in only - no signup tab per funnel optimization)
  return (
    <>
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-500/5 via-background to-cyan-500/5 dark:from-purple-500/10 dark:via-background dark:to-cyan-500/10 relative overflow-hidden">
      <LandingHeader />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4 relative z-10 animate-fade-in">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="gap-2 hover-lift border-border bg-card text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <Card className="w-full glass-card shadow-glass border border-border/50 shadow-[0_0_20px_hsl(var(--primary)/0.1),0_8px_32px_hsl(var(--primary)/0.08)] backdrop-blur-sm transition-shadow hover:shadow-[0_0_25px_hsl(var(--primary)/0.15),0_12px_40px_hsl(var(--primary)/0.12)]">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            {pendingInvitation ? "Join Your Team" : "Sign in to Auditic"}
          </CardTitle>
          <CardDescription>
            {pendingInvitation ? (
              <>
                You've been invited to join <strong>{pendingInvitation.companies?.name}</strong>
              </>
            ) : (
              "Access your saved discoveries, projects, and outputs."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Google Sign In - Primary CTA */}
          <Button
            type="button"
            className="w-full h-12 gap-2 bg-foreground text-background hover:bg-foreground/90 border-0"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {loading ? "Connecting..." : "Continue with Google"}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or sign in with email</span>
            </div>
          </div>

          {/* Sign In Form */}
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signin-password">Password</Label>
              <Input
                id="signin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                  <Label htmlFor="remember-me" className="text-sm cursor-pointer">
                    Remember me
                  </Label>
                </div>
                <Button
                  type="button"
                  variant="link"
                  className="text-sm px-0 h-auto"
                  onClick={() => setShowPasswordReset(true)}
                >
                  Forgot Password?
                </Button>
              </div>
            </div>
            <Button type="submit" variant="outline" className="w-full border-foreground/30 text-foreground hover:bg-foreground hover:text-background" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Separator */}
          <div className="relative pt-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
          </div>

          {/* New users redirect - This is critical to prevent funnel theft */}
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground mb-2">New to Auditic?</p>
            <Button
              variant="link"
              className="text-primary font-medium p-0 h-auto"
              onClick={() => navigate("/auth?tab=signup")}
            >
              → Start for Free
            </Button>
          </div>
        </CardContent>
          </Card>
          
          {/* Small reassurance text */}
          <p className="text-center text-xs text-muted-foreground/60 mt-4">
            Auditic is built for consultants and agencies who need faster, clearer discovery.
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
